import { useEffect, useState } from "react";
import axios from "axios";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Users, Phone, Mail, Calendar, Search, User, Shield, Package, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

export const AdminUsers = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API}/admin/users`);
            setUsers(response.data);
        } catch (error) {
            console.error("Error fetching users:", error);
            toast.error("Pa kapab chaje lis kliyan yo.");
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "N/A";
        return new Date(dateStr).toLocaleDateString('fr-HT', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const filteredUsers = users.filter(user => 
        (user.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.email || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.phone || "").includes(searchTerm)
    );

    return (
        <AdminLayout>
            <div className="space-y-8">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div>
                        <h1 className="text-4xl font-black text-[#232F3E] tracking-tighter italic uppercase leading-none">
                            Jesyon <span className="text-indigo-600">Kliyan</span>
                        </h1>
                        <p className="text-gray-400 font-bold uppercase tracking-widest text-[9px] mt-2">
                            Lis tout itilizatè ki anrejistre sou sistèm LinkAmazon an.
                        </p>
                    </div>
                    
                    <div className="relative w-full md:w-96">
                        <Search className="absolute left-6 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <Input
                            placeholder="Chèche pa non, email oswa telefòn..."
                            className="h-16 rounded-[25px] bg-white border-0 shadow-2xl pl-16 pr-8 font-black text-xs uppercase tracking-wider text-gray-900 focus:ring-0 focus:border-indigo-600 transition-all"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                {loading ? (
                    <div className="flex justify-center py-32">
                        <div className="w-14 h-14 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                ) : (
                    <Card className="border-0 shadow-[0_50px_100px_-20px_rgba(0,0,0,0.15)] rounded-[45px] overflow-hidden bg-white">
                        <CardContent className="p-0">
                            {filteredUsers.length > 0 ? (
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead>
                                            <tr className="bg-gray-50/50 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] border-b border-gray-100/50">
                                                <th className="py-8 px-12 text-left">Itilizatè</th>
                                                <th className="py-8 px-8 text-left">Kontak</th>
                                                <th className="py-8 px-8 text-left">Depatman</th>
                                                <th className="py-8 px-8 text-left">Dat Antre</th>
                                                <th className="py-8 px-12 text-center">Verifye</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-50">
                                            {filteredUsers.map((user) => (
                                                <tr key={user.id} className="hover:bg-gray-50/80 transition-all border-b border-gray-50 last:border-0 group">
                                                    <td className="py-8 px-12">
                                                        <div className="flex items-center gap-6">
                                                            <div className="w-16 h-16 rounded-[22px] bg-gradient-to-br from-indigo-50 to-white shadow-inner flex items-center justify-center font-black text-2xl text-indigo-600 group-hover:scale-110 transition-transform duration-500">
                                                                {(user.name || user.email || "?")[0].toUpperCase()}
                                                            </div>
                                                            <div className="flex flex-col">
                                                                <span className="font-black text-gray-900 text-lg tracking-tight line-clamp-1">{user.name || "Itilizatè Enkoni"}</span>
                                                                <span className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1">ID: {user.id.slice(0, 8)}</span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="py-8 px-8 text-sm">
                                                        <div className="space-y-2">
                                                            <div className="flex items-center gap-3 text-gray-600 font-bold">
                                                                <Mail className="w-3.5 h-3.5 text-gray-300" /> {user.email}
                                                            </div>
                                                            {user.phone && (
                                                                <div className="flex items-center gap-3 text-gray-600 font-bold">
                                                                    <Phone className="w-3.5 h-3.5 text-gray-300" /> {user.phone}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="py-8 px-8">
                                                        <span className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-50 border border-gray-100 text-[10px] font-black text-gray-400 uppercase tracking-widest italic shadow-sm group-hover:bg-indigo-50 group-hover:text-indigo-600 group-hover:border-indigo-100 transition-colors">
                                                            <Shield className="w-3 h-3" /> LinkAmazon Buyer
                                                        </span>
                                                    </td>
                                                    <td className="py-8 px-8 text-[11px] font-black text-gray-300 uppercase tracking-tighter">
                                                        <div className="flex items-center gap-2">
                                                            <Calendar className="w-3.5 h-3.5 opacity-30" /> {formatDate(user.created_at)}
                                                        </div>
                                                    </td>
                                                    <td className="py-8 px-12">
                                                        <div className="flex items-center justify-center gap-2">
                                                            <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center text-green-500 shadow-inner">
                                                                <Shield className="w-4 h-4" />
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="text-center py-48 text-gray-400 flex flex-col items-center">
                                    <Users className="w-24 h-24 mb-8 opacity-5 italic" />
                                    <p className="font-black uppercase tracking-widest text-[11px] max-w-xs leading-loose">Pa gen okenn itilizatè ki koresponn ak rechèch ou an.</p>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                )}
            </div>
        </AdminLayout>
    );
};
